// src/components/FlightStatusCard.js
import React, { useState } from 'react';
import { Card, CardContent, Typography, Collapse, Box, CircularProgress } from '@mui/material';

function normalizeStatus(s) {
  switch (s) {
    // New vocabulary
    case 'go':                     return 'go';
    case 'advisory':               return 'advisory';
    case 'caution':                return 'caution';
    case 'no-go':                  return 'no-go';
    case 'unknown':                return 'unknown';
    // Legacy vocabulary (fall-through)
    case 'clear_to_fly':           return 'go';
    case 'clear_with_advisories':  return 'advisory';
    case 'not_recommended':        return 'caution';
    case 'do_not_fly':             return 'no-go';
    case 'illegal_to_fly':         return 'illegal'; // special styling
    default:                       return 'unknown';
  }
}

const FLIGHT_DISPLAY = {
  go: {
    summary: '✅ CLEAR TO FLY',
    detail:  'Conditions are optimal. No known airspace or weather issues.',
    color:   '#00E676',
    progress: 100,
  },
  advisory: {
    summary: '⚠️ CLEAR TO FLY — ADVISORIES',
    detail:  'Flyable, but there are minor advisories to consider.',
    color:   '#FFEA00',
    progress: 75,
  },
  caution: {
    summary: '🔶 FLIGHT NOT RECOMMENDED',
    detail:  'Risky flight. Conditions not ideal for your drone.',
    color:   '#FF9100',
    progress: 50,
  },
  'no-go': {
    summary: '❌ DO NOT FLY',
    detail:  'Conditions are unsafe for flight.',
    color:   '#FF5252',
    progress: 25,
  },
  illegal: {
    summary: '⛔ DO NOT FLY — LEGAL RESTRICTION',
    detail:  'Airspace restrictions prohibit flight in this area.',
    color:   '#D50000',
    progress: 10,
  },
  unknown: {
    summary: 'NO DATA YET',
    detail:  'There is not enough data yet to make a firm decision.',
    color:   '#999999',
    progress: 0,
  },
};

const FlightStatusCard = ({ status, reason }) => {
  const [expanded, setExpanded] = useState(false);
  const sev = normalizeStatus(status);
  const cfg = FLIGHT_DISPLAY[sev] ?? FLIGHT_DISPLAY.unknown;

  const detailText =
    sev === 'no-go'
      ? `${cfg.detail}${reason ? ' ' + reason : ''}`
      : cfg.detail;

  return (
    <Card onClick={() => setExpanded(!expanded)} sx={{ cursor: 'pointer' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', mr: 2 }}>
          <CircularProgress
            variant="determinate"
            value={cfg.progress}
            size={64}
            thickness={4}
            sx={{ color: cfg.color }}
          />
          <Box
            sx={{
              top: 0, left: 0, bottom: 0, right: 0,
              position: 'absolute', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Typography variant="subtitle2" component="div" sx={{ color: cfg.color }}>
              {cfg.progress}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Flight Clearance</Typography>
          <Typography variant="h5" sx={{ color: cfg.color }}>
            {cfg.summary}
          </Typography>
        </Box>
      </CardContent>
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#c1c6d6' }}>{detailText}</Typography>
        </Box>
      </Collapse>
    </Card>
  );
};

export default FlightStatusCard;
